import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth } from '../lib/auth';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { ShieldCheck, Users, Radio, Gavel, Package, Mail, CheckCircle2, ScrollText, Wallet } from 'lucide-react';
import { CommissionPanel } from '../components/CommissionPanel';
import { PaymentSettings } from '../components/PaymentSettings';
import { VideoSettings } from '../components/VideoSettings';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { UsersPanel } from '../components/UsersPanel';
import { RebrandBroadcast } from '../components/RebrandBroadcast';
import { BroadcastPanel } from '../components/BroadcastPanel';

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [stats, setStats] = useState({});
  const [contacts, setContacts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [autoApprove, setAutoApprove] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: st }, { data: c }, { data: a }, { data: ap }] = await Promise.all([
        api.get('/admin/sellers'),
        api.get('/admin/stats'),
        api.get('/admin/contacts'),
        api.get('/admin/audit'),
        api.get('/admin/settings/seller-approval'),
      ]);
      setSellers(s); setStats(st); setContacts(c); setAudit(a); setAutoApprove(ap.auto_approve);
    } catch (e) {}
  };
  const toggleAuto = async () => {
    try {
      const { data } = await api.post('/admin/settings/seller-approval', { enabled: !autoApprove });
      setAutoApprove(data.auto_approve);
      toast.success(data.auto_approve ? `Validation automatique activée${data.approved_now ? ` — ${data.approved_now} vendeuse(s) en attente validée(s)` : ''}` : 'Validation manuelle');
      load();
    } catch (e) { toast.error('Erreur'); }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);
  if (!user || user.role !== 'admin') return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Admin only</div></div>;

  const act = async (uid, action) => {
    try { await api.post(`/admin/sellers/${uid}/${action}`); toast.success('OK'); load(); } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-rose-400" />{t('admin_dashboard')}</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { k: 'users', label: 'Users', icon: <Users className="h-4 w-4" /> },
            { k: 'sellers_pending', label: 'Pending', icon: <Users className="h-4 w-4" /> },
            { k: 'lives_active', label: 'Lives', icon: <Radio className="h-4 w-4" /> },
            { k: 'auctions_running', label: 'Auctions', icon: <Gavel className="h-4 w-4" /> },
            { k: 'commission_pending', label: 'Commission due', icon: <Wallet className="h-4 w-4" />, fmt: fcfa },
          ].map((c) => (
            <div key={c.k} className="card-elev rounded-2xl p-4" data-testid={`stat-${c.k}`}>
              <div className="text-xs text-slate-400 flex items-center gap-1">{c.icon}{c.label}</div>
              <div className="font-mono-num text-3xl font-extrabold text-amber-400 mt-1">{stats[c.k] == null ? '—' : c.fmt ? c.fmt(stats[c.k]) : stats[c.k]}</div>
            </div>
          ))}
        </div>

        <BroadcastPanel />

        <RebrandBroadcast />

        <UsersPanel />

        <CommissionPanel admin />
        <PaymentSettings />
        <VideoSettings />

        <section className="card-elev rounded-2xl p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-xl font-bold">Vendeuses</h2>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="auto-approve-toggle-label">
              <span className="text-slate-300">Validation automatique</span>
              <button type="button" role="switch" aria-checked={autoApprove} data-testid="auto-approve-toggle" onClick={toggleAuto}
                className={`relative h-6 w-11 rounded-full transition-colors ${autoApprove ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${autoApprove ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-xs text-slate-500" data-testid="auto-approve-status">{autoApprove ? 'ON — les nouvelles vendeuses peuvent vendre immédiatement' : 'OFF — validation manuelle'}</span>
            </label>
          </div>
          <div className="space-y-2">
            {sellers.map((s) => (
              <div key={s.user_id} className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between text-sm">
                <div>
                  <div className="font-bold flex items-center gap-2">{s.name}{s.verified && <VerifiedBadge verified testId={`admin-verified-${s.user_id}`} />}</div>
                  <div className="text-slate-400 text-xs">{s.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.approved ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                    {s.approved ? 'approved' : 'pending'}
                  </span>
                  {!s.verified && <Button data-testid={`approve-${s.user_id}`} size="sm" onClick={() => act(s.user_id, 'approve')} className="bg-emerald-600 text-white">{s.approved ? 'Vérifier' : t('approve')}</Button>}
                  {s.verified && <Button data-testid={`unverify-${s.user_id}`} size="sm" variant="outline" onClick={() => act(s.user_id, 'unverify')} className="border-slate-700 text-slate-100">Retirer badge</Button>}
                  {s.approved && <Button data-testid={`suspend-${s.user_id}`} size="sm" variant="outline" onClick={() => act(s.user_id, 'suspend')} className="border-rose-500/50 text-rose-300">{t('suspend')}</Button>}
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="card-elev rounded-2xl p-5">
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><Mail className="h-5 w-5 text-rose-400" />Messages ({contacts.filter(c => !c.read).length} non lus)</h2>
          <div className="space-y-2">
            {contacts.length === 0 && <div className="text-slate-500 text-sm">Aucun message pour l'instant.</div>}
            {contacts.map((c) => (
              <div key={c.contact_id} data-testid={`contact-${c.contact_id}`}
                className={`p-3 rounded-xl border text-sm ${c.read ? 'bg-slate-900/40 border-slate-800' : 'bg-rose-500/5 border-rose-500/30'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-bold">{c.subject}</div>
                    <div className="text-xs text-slate-400">{c.name} — <a href={`mailto:${c.email}`} className="hover:text-rose-400">{c.email}</a></div>
                  </div>
                  <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="mt-2 text-slate-300 whitespace-pre-line">{c.message}</div>
                {!c.read && (
                  <Button data-testid={`contact-read-${c.contact_id}`} size="sm" variant="outline"
                    onClick={async () => { try { await api.post(`/admin/contacts/${c.contact_id}/read`); load(); } catch {} }}
                    className="mt-2 border-slate-700 text-slate-100 bg-transparent">
                    <CheckCircle2 className="h-3 w-3 mr-1" />Marquer lu
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="card-elev rounded-2xl p-5">
          <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2"><ScrollText className="h-5 w-5 text-rose-400" />Journal d'audit ({audit.length})</h2>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/30 text-sm" data-testid="remind-sellers-box">
            <span className="text-slate-300">Relance vendeurs : e-mail + notification aux boutiques sans produits, sans photos, sans prix/stock ou sans description.</span>
            <Button size="sm" data-testid="admin-remind-sellers-btn" className="btn-gold font-bold"
              onClick={async () => { try { const { data } = await api.post('/admin/remind-sellers'); toast.success(`${data.notified} vendeur(s) relancé(s), ${data.emailed} e-mail(s) envoyé(s) sur ${data.sellers_checked} vérifié(s)`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } }}>
              Relancer les vendeurs
            </Button>
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-sm">
            <span className="text-slate-300">Nettoyage : termine les lives de test (titre « TEST… ») et les lives caméra inactifs depuis plus de 24 h.</span>
            <Button size="sm" data-testid="admin-cleanup-btn" className="btn-brand text-white"
              onClick={async () => { try { const { data } = await api.post('/admin/cleanup'); toast.success(`Nettoyé : ${data.ended_test + data.ended_stale} live(s), ${data.removed_products} article(s)`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } }}>
              Nettoyer les données de test
            </Button>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto pretty-scroll">
            {audit.length === 0 && <div className="text-slate-500 text-sm">Aucune action enregistrée.</div>}
            {audit.map((a) => (
              <div key={a.audit_id} data-testid={`audit-${a.audit_id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-900/40 border border-slate-800 text-xs">
                <div className="flex-1 min-w-0">
                  <span className="font-mono-num text-rose-400 mr-2">{a.action}</span>
                  <span className="text-slate-300">{a.actor_email}</span>
                  {a.target && <span className="text-slate-500 ml-1">→ {a.target}</span>}
                </div>
                <span className="text-slate-500 flex-shrink-0">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
